import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, Info, BookOpen, Search, X, Compass } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useHelpDrawer } from './HelpDrawerContext';
import { getHelpContent } from '@/data/help-content';
import { useCurrentUserPermissions } from '@/hooks/use-current-user-permissions';
import { FunctionalityTab } from './FunctionalityTab';
import { UserGuideTab } from './UserGuideTab';
import { FAQTab } from './FAQTab';
import { ToursTab } from './ToursTab';
import { cn } from '@/lib/utils';
import type { UserSegment } from '@/types/product-tour';

// Icon colours mapped to page types
const PAGE_ICON_COLOURS: Record<string, string> = {
  settings:                    'bg-purple-500',
  'settings-general':          'bg-purple-500',
  'settings-project':          'bg-blue-500',
  'settings-user-permissions': 'bg-error-500',
  'settings-integrations':     'bg-success-500',
  'settings-notifications':    'bg-orange-500',
  'settings-user-preferences': 'bg-teal-500',
  'settings-templates':        'bg-indigo-500',
  projects:                    'bg-sky-500',
};

export function HelpDrawer() {
  const { isOpen, pageKey, closeHelp } = useHelpDrawer();
  const { permissions } = useCurrentUserPermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const content = getHelpContent(pageKey);
  const iconColour = PAGE_ICON_COLOURS[pageKey] ?? 'bg-primary';

  // Derive the tour UserSegment from the user's permissions so the Tours tab
  // only shows tours relevant to this user (avoids duplicate tours appearing).
  const userSegment: UserSegment = permissions?.reports?.executiveDashboard
    ? 'admin'
    : permissions?.reports?.pmDashboard
      ? 'manager'
      : 'employee';

  // Reset search when page key changes
  useEffect(() => {
    setSearchQuery('');
  }, [pageKey]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeHelp();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeHelp]);

  const drawer = (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 transition-opacity duration-300"
          onClick={closeHelp}
          aria-hidden="true"
        />
      )}

      {/* Drawer panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Help"
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex flex-col bg-background shadow-xl border-l border-border',
          'transition-transform ease-in-out duration-300',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
        style={{ width: '600px', maxWidth: '100vw' }}
      >
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="shrink-0 bg-muted/40 border-b border-border px-5 py-4 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                  iconColour,
                )}
              >
                <HelpCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                {content ? (
                  <>
                    <h2 className="text-base font-semibold leading-snug">{content.pageTitle}</h2>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug max-w-[420px]">
                      {content.description}
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-base font-semibold">Help</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Help content for this page is coming soon.
                    </p>
                  </>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={closeHelp}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close help</span>
            </Button>
          </div>

          {/* Search */}
          {content && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search help content…"
                className="pl-8 h-8 text-sm pr-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Tabs + Body ─────────────────────────────────────────────── */}
        {content ? (
          <Tabs defaultValue="functionality" className="flex flex-col flex-1 min-h-0">
            <div className="shrink-0 px-5 pt-3 border-b border-border bg-background">
              <TabsList className="h-9 gap-1 bg-transparent p-0">
                <TabsTrigger
                  value="functionality"
                  className="gap-1.5 data-[state=active]:bg-muted data-[state=active]:shadow-none rounded-md px-3 h-8 text-sm"
                >
                  <Info className="h-3.5 w-3.5" />
                  Functionality
                </TabsTrigger>
                <TabsTrigger
                  value="guide"
                  className="gap-1.5 data-[state=active]:bg-muted data-[state=active]:shadow-none rounded-md px-3 h-8 text-sm"
                >
                  <BookOpen className="h-3.5 w-3.5" />
                  User Guide
                </TabsTrigger>
                <TabsTrigger
                  value="faq"
                  className="gap-1.5 data-[state=active]:bg-muted data-[state=active]:shadow-none rounded-md px-3 h-8 text-sm"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                  FAQ
                </TabsTrigger>
                <TabsTrigger
                  value="tours"
                  className="gap-1.5 data-[state=active]:bg-muted data-[state=active]:shadow-none rounded-md px-3 h-8 text-sm"
                >
                  <Compass className="h-3.5 w-3.5" />
                  Tours
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Scrollable tab body */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <TabsContent value="functionality" className="m-0 p-5 h-full">
                <FunctionalityTab
                  content={content}
                  permissions={permissions}
                  searchQuery={searchQuery}
                />
              </TabsContent>
              <TabsContent value="guide" className="m-0 p-5 h-full">
                <UserGuideTab
                  content={content}
                  permissions={permissions}
                  searchQuery={searchQuery}
                />
              </TabsContent>
              <TabsContent value="faq" className="m-0 p-5 h-full">
                <FAQTab
                  content={content}
                  permissions={permissions}
                  searchQuery={searchQuery}
                />
              </TabsContent>
              <TabsContent value="tours" className="m-0 p-5 h-full">
                <ToursTab pageKey={pageKey} onClose={closeHelp} userSegment={userSegment} />
              </TabsContent>
            </div>

            {/* ── Footer ──────────────────────────────────────────────── */}
            {content.relatedPages.length > 0 && (
              <div className="shrink-0 border-t border-border bg-muted/40 px-5 py-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Related Pages
                </p>
                <div className="flex flex-wrap gap-2">
                  {content.relatedPages.map((page) => (
                    <Button
                      key={page.path}
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        navigate(page.path);
                        closeHelp();
                      }}
                    >
                      {page.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </Tabs>
        ) : (
          /* No content placeholder */
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center space-y-2 max-w-xs">
              <HelpCircle className="h-10 w-10 text-muted-foreground/40 mx-auto" />
              <p className="text-sm font-medium text-muted-foreground">Help content coming soon</p>
              <p className="text-xs text-muted-foreground">
                Detailed help documentation for this page will be available in a future update.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return createPortal(drawer, document.body);
}
